import React, { useEffect, useState } from 'react';
import './usersVisitors.css';
import UserJourneyDrawer from './UserJourneyDrawer';
import { getAnalyticsUsers, getAnalyticsUserDetails } from '../../../api/analytics';
import Pagination from '../../pagination/Pagination';

// fetchUsers / fetchUserDetails default to the support-admin (platform-wide)
// endpoints. The organizer analytics page passes the org-scoped equivalents
// so this same component works for both dashboards.
const UsersVisitors = ({ data = {}, from, to, fetchUsers: fetchUsersProp, fetchUserDetails }) => {
    const fetchUsersFn = fetchUsersProp || getAnalyticsUsers;
    const fetchUserDetailsFn = fetchUserDetails || getAnalyticsUserDetails;

    const { summary = {}, users: initialUsers = [], pagination: initialPagination = {} } = data;

    const [users, setUsers] = useState(initialUsers);
    const [selectedUser, setSelectedUser] = useState(null);

    // -----------------------------------------
    // Pagination
    // -----------------------------------------
    const [limit] = useState(20);
    const [offSet, setOffSet] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [totalData, setTotalData] = useState(initialPagination.total || 0);

    const { uniqueUsers = 0, anonymousVisitors = 0, totalVisitors = 0 } = summary;

    // -----------------------------------------
    // Fetch a page of users. Only called for pages after the
    // first - the first page arrives via the "data" prop, which
    // the parent already fetched alongside the rest of the
    // dashboard, so we don't hit the endpoint twice on load.
    // -----------------------------------------
    const fetchUsers = async (offset) => {
        try {
            const response = await fetchUsersFn({
                from,
                to,
                limit,
                offset
            });

            const result = response?.data?.data;

            setUsers(result?.users || []);
            setTotalData(result?.pagination?.total || 0);
        } catch (error) {
            console.error('Failed to fetch analytics users:', error);

            setUsers([]);
            setTotalData(0);
        }
    };

    // -----------------------------------------
    // Initial load / date filter change - reset to the first
    // page using the data the parent already fetched instead of
    // firing a duplicate request for the same page.
    // -----------------------------------------
    useEffect(() => {
        setOffSet(0);
        setPageNumber(1);
        setUsers(data?.users || []);
        setTotalData(data?.pagination?.total || 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, from, to]);

    // -----------------------------------------
    // Pagination click
    // -----------------------------------------
    const paginationClick = (page, offset) => {
        setPageNumber(page);
        setOffSet(offset);

        fetchUsers(offset);
    };

    return (
        <section className='usersVisitors'>
            {/* -----------------------------------------
                Header
            ----------------------------------------- */}
            <div className='usersVisitorsHeader'>
                <div>
                    <h2>Users / Visitors</h2>
                    <p>Visitor activity and booking behaviour</p>
                </div>
            </div>

            {/* -----------------------------------------
                Summary
            ----------------------------------------- */}
            <div className='usersVisitorsSummary'>
                <div className='usersVisitorsCard'>
                    <span className='usersVisitorsCardLabel'>Logged-in Users</span>

                    <strong>{uniqueUsers}</strong>
                </div>

                <div className='usersVisitorsCard'>
                    <span className='usersVisitorsCardLabel'>Anonymous Visitors</span>

                    <strong>{anonymousVisitors}</strong>
                </div>

                <div className='usersVisitorsCard'>
                    <span className='usersVisitorsCardLabel'>Total Visitors</span>

                    <strong>{totalVisitors}</strong>
                </div>
            </div>

            {/* -----------------------------------------
                Visitor Activity
            ----------------------------------------- */}
            <div className='usersVisitorsTableWrapper'>
                <div className='usersVisitorsTableHeader'>
                    <h3>Visitor Activity</h3>
                </div>

                {users.length === 0 ? (
                    <div className='usersVisitorsEmpty'>No user activity found for this period.</div>
                ) : (
                    <div className='usersVisitorsTableScroll'>
                        <table className='usersVisitorsTable'>
                            <thead>
                                <tr>
                                    <th className='userColumn'>User / Visitor</th>

                                    <th className='platformColumn'>Platform</th>

                                    <th>Sessions</th>

                                    <th>Searches</th>

                                    <th>Trip Views</th>

                                    <th>Paid</th>
                                </tr>
                            </thead>

                            <tbody>
                                {users.map((user, index) => {
                                    const isUser = user.type === 'user';

                                    const userId = String(user.id || '');

                                    return (
                                        <tr
                                            key={`${user.type}-${user.id}-${index}`}
                                            className='clickableUserRow'
                                            onClick={() => setSelectedUser(user)}
                                        >
                                            {/* User / Visitor */}
                                            <td className='userColumn'>
                                                <div className='visitorIdentity'>
                                                    <span className='visitorIdentityName'>
                                                        {isUser
                                                            ? user.name || `User #${userId.slice(-6)}`
                                                            : `Visitor #${userId.slice(-6)}`}
                                                    </span>

                                                    <span
                                                        className={`visitorType ${isUser ? 'loggedIn' : 'anonymous'}`}
                                                    >
                                                        {isUser ? 'Logged in' : 'Anonymous'}
                                                    </span>
                                                </div>
                                            </td>

                                            {/* Platform */}
                                            <td className='platformColumn'>
                                                <div className='platformList'>
                                                    {(user.platforms || []).map((platform) => (
                                                        <span
                                                            key={platform}
                                                            className='platformBadge'
                                                            data-platform={String(platform).toLowerCase()}
                                                        >
                                                            {platform}
                                                        </span>
                                                    ))}
                                                </div>
                                            </td>

                                            {/* Sessions */}
                                            <td>{user.sessions || 0}</td>

                                            {/* Searches */}
                                            <td>{user.searches || 0}</td>

                                            {/* Trip Views */}
                                            <td>{user.tripViews || 0}</td>

                                            {/* Paid */}
                                            <td>
                                                <span className={user.paid > 0 ? 'paidValue' : 'noPaidValue'}>
                                                    {user.paid || 0}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* -----------------------------------------
                    Pagination
                ----------------------------------------- */}
                {totalData > limit && (
                    <Pagination
                        paginationClick={paginationClick}
                        numberOfRows={totalData}
                        offSet={offSet}
                        pageNumber={pageNumber}
                        limit={limit}
                    />
                )}
            </div>

            {/* -----------------------------------------
                User Journey
            ----------------------------------------- */}
            {selectedUser && (
                <UserJourneyDrawer
                    user={selectedUser}
                    from={from}
                    to={to}
                    onClose={() => setSelectedUser(null)}
                    fetchUserDetails={fetchUserDetailsFn}
                />
            )}
        </section>
    );
};

export default UsersVisitors;
